/**
 * IB Space Vuex Store Module
 * Manages IB-specific state: active community, partner branding, UI
 */
const ibModule = {
  namespaced: true,

  state: {
    // Active community context
    activeCommunity: null,       // The selected club object
    activeMembership: null,      // The user's membership { id, rol, club }
    userCommunities: [],         // All user's communities from API
    communityMemberCount: 0,     // Member count for active community
    communitySponsor: null,      // Main partner/sponsor for active community

    // Partner branding (from whitelabel API, clean names)
    partner: {
      id: null,
      name: '',
      logoUrl: '',
      responsiveLogoUrl: '',
      websiteUrl: '',
      primaryColor: '',
      themes: { light: {}, dark: {} }
    },

    // UI
    loading: false
  },

  mutations: {
    SET_ACTIVE_COMMUNITY(state, community) {
      state.activeCommunity = community
    },

    SET_ACTIVE_MEMBERSHIP(state, membership) {
      state.activeMembership = membership
    },

    SET_USER_COMMUNITIES(state, communities) {
      state.userCommunities = communities
    },

    SET_COMMUNITY_MEMBER_COUNT(state, count) {
      state.communityMemberCount = count
    },

    SET_COMMUNITY_SPONSOR(state, sponsor) {
      state.communitySponsor = sponsor
    },

    SET_PARTNER(state, partner) {
      state.partner = { ...state.partner, ...partner }
    },

    SET_LOADING(state, loading) {
      state.loading = loading
    },

    CLEAR_COMMUNITY(state) {
      state.activeCommunity = null
      state.activeMembership = null
      state.communityMemberCount = 0
      state.communitySponsor = null
    },

    CLEAR_ALL(state) {
      state.activeCommunity = null
      state.activeMembership = null
      state.userCommunities = []
      state.communityMemberCount = 0
      state.communitySponsor = null
      state.partner = {
        id: null,
        name: '',
        logoUrl: '',
        responsiveLogoUrl: '',
        websiteUrl: '',
        primaryColor: '',
        themes: { light: {}, dark: {} }
      }
      state.loading = false
    }
  },

  actions: {
    async selectCommunity({ commit, dispatch }, { community, membership }) {
      commit('SET_ACTIVE_COMMUNITY', community)
      commit('SET_ACTIVE_MEMBERSHIP', membership)
      dispatch('fetchMemberCount', community.id)
      dispatch('fetchCommunitySponsor', community.id)
    },

    async fetchCommunitySponsor({ commit }, clubId) {
      try {
        const ClubRepository = require('@/repositories/club').default
        const response = await ClubRepository.getClubPartners(clubId)
        if (response.status < 400 && response.data) {
          const data = response.data
          const mainPartner = data.partners
            ? data.partners.find(p => p.id === data.mainPartnerId) || data.partners[0]
            : null
          if (mainPartner) {
            commit('SET_COMMUNITY_SPONSOR', {
              id: mainPartner.id,
              name: mainPartner.shortName || mainPartner.fullName || '',
              logoUrl: mainPartner.responsiveLogoUrl || mainPartner.fullLogoUrl || '',
              brandLogo: mainPartner.partnerBrand?.responsiveLogo || mainPartner.partnerBrand?.fullLogo || ''
            })
          }
        }
      } catch (error) {
        console.error('[IB Store] Failed to fetch community sponsor:', error)
      }
    },

    async fetchMemberCount({ commit }, clubId) {
      try {
        const ClubRepository = require('@/repositories/club').default
        const response = await ClubRepository.getAllClubMembers(clubId)
        if (response.status < 400 && response.data) {
          const activeMembers = response.data.filter(m => m.rol !== 'PENDING')
          commit('SET_COMMUNITY_MEMBER_COUNT', activeMembers.length)
        }
      } catch (error) {
        console.error('[IB Store] Failed to fetch member count:', error)
      }
    },

    quitCommunity({ commit }) {
      commit('CLEAR_COMMUNITY')
    },

    setUserCommunities({ commit }, communities) {
      commit('SET_USER_COMMUNITIES', communities)
    },

    setPartnerBranding({ commit }, partner) {
      commit('SET_PARTNER', partner)
    },

    logout({ commit }) {
      commit('CLEAR_ALL')
    }
  },

  getters: {
    activeCommunity: (state) => state.activeCommunity,
    activeMembership: (state) => state.activeMembership,
    userCommunities: (state) => state.userCommunities,
    partner: (state) => state.partner,
    partnerName: (state) => state.partner.name || '',
    partnerLogoUrl: (state) => state.partner.responsiveLogoUrl || state.partner.logoUrl || '',
    isLoading: (state) => state.loading,
    hasCommunity: (state) => state.activeCommunity !== null,
    memberCount: (state) => state.communityMemberCount,
    communitySponsor: (state) => state.communitySponsor,
    communityColor: (state) => {
      if (!state.activeCommunity) return '6366F1'
      return state.activeCommunity.primaryColor || '6366F1'
    }
  }
}

export default ibModule
